import { useMemo, useState } from 'react';
import {
  ALL_ASSISTANT_PRIVILEGES,
  DEFAULT_ASSISTANT_PRIVILEGES,
  PRIVILEGE_MODULES,
} from '../types/assistantPrivileges';

interface AssistantPrivilegePickerProps {
  selected: string[];
  onChange: (privileges: string[]) => void;
  disabled?: boolean;
}

const AssistantPrivilegePicker = ({
  selected,
  onChange,
  disabled = false,
}: AssistantPrivilegePickerProps) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PRIVILEGE_MODULES;
    return PRIVILEGE_MODULES.map((mod) => ({
      ...mod,
      privileges: mod.privileges.filter(
        (p) =>
          mod.label.toLowerCase().includes(q) ||
          p.label.toLowerCase().includes(q) ||
          p.value.toLowerCase().includes(q)
      ),
    })).filter((mod) => mod.privileges.length > 0);
  }, [search]);

  const togglePrivilege = (value: string) => {
    if (disabled) return;
    if (selectedSet.has(value)) {
      onChange(selected.filter((p) => p !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const toggleModule = (moduleKey: string, values: string[]) => {
    if (disabled) return;
    const allSelected = values.every((v) => selectedSet.has(v));
    if (allSelected) {
      onChange(selected.filter((p) => !values.includes(p)));
    } else {
      const next = new Set(selected);
      values.forEach((v) => next.add(v));
      onChange(Array.from(next));
    }
  };

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => onChange([...ALL_ASSISTANT_PRIVILEGES]);
  const selectNone = () => onChange([]);
  const selectDefaults = () => onChange([...DEFAULT_ASSISTANT_PRIVILEGES]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="block text-sm font-medium text-gray-700">
          الصلاحيات ({selected.length})
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={selectDefaults}
            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            الافتراضي
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={selectAll}
            className="text-xs px-2 py-1 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            تحديد الكل
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={selectNone}
            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            إلغاء الكل
          </button>
        </div>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="بحث في الصلاحيات..."
        className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
      />

      <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
        {filteredModules.map((mod) => {
          const values = mod.privileges.map((p) => p.value);
          const selectedCount = values.filter((v) => selectedSet.has(v)).length;
          const allSelected = selectedCount === values.length && values.length > 0;
          const isCollapsed = collapsed.has(mod.key);

          return (
            <div key={mod.key} className="bg-white">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                <button
                  type="button"
                  onClick={() => toggleCollapse(mod.key)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-800"
                >
                  <span className="text-gray-400">{isCollapsed ? '▸' : '▾'}</span>
                  {mod.label}
                  <span className="text-xs text-gray-500">
                    ({selectedCount}/{values.length})
                  </span>
                </button>
                <label className="flex items-center gap-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={disabled}
                    onChange={() => toggleModule(mod.key, values)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  الكل
                </label>
              </div>
              {!isCollapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 px-3 py-2">
                  {mod.privileges.map((p) => (
                    <label
                      key={p.value}
                      className="flex items-center gap-2 text-sm text-gray-700 py-1 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSet.has(p.value)}
                        disabled={disabled}
                        onChange={() => togglePrivilege(p.value)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <span>{p.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssistantPrivilegePicker;
